import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { logAudioError } from '../services/errorMonitoring';

/**
 * 🎵 RADIO PLAYER CONTEXT - VERSÃO CORRIGIDA
 * 
 * Contexto global para gerenciar o player da Rádio do Compositor.
 * Garante que:
 * - Apenas UMA música toque por vez
 * - O player persiste durante a navegação
 * - O usuário sempre tem controle (play/pause/stop/volume)
 * - O player só desaparece quando o usuário fecha explicitamente
 * - A música continua tocando automaticamente seguindo a playlist
 * - 🐛 ERROS SÃO REPORTADOS AO SISTEMA DE MONITORAMENTO
 * - 🔧 URLs SÃO CORRIGIDAS AUTOMATICAMENTE SE NECESSÁRIO
 * 
 * CORREÇÕES APLICADAS:
 * - Uso de useRef para playlist e currentIndex para evitar stale closures
 * - handleEnded agora acessa valores atuais corretamente
 * - Auto-play da próxima música funciona em qualquer página
 * - Erros de áudio são logados no sistema de monitoramento
 * - URLs incorretas são corrigidas automaticamente
 * - 🔧 VERSÃO ATUAL: Elemento de áudio GLOBAL para persistir durante navegação
 */

// 🔧 VERSÃO ATUAL: ELEMENTO DE ÁUDIO GLOBAL
// Criado FORA do React para garantir persistência durante navegação
let globalAudioElement = null;
let globalAudioState = {
  currentTrack: null,
  isPlaying: false,
  playlist: [],
  currentIndex: -1,
  volume: 0.8
};

// Função para obter o elemento de áudio global
const getGlobalAudio = () => {
  if (!globalAudioElement) {
    console.log('🎵 [GLOBAL] Criando Audio Element Global (fora do React)...');
    globalAudioElement = new Audio();
    globalAudioElement.volume = globalAudioState.volume;
    globalAudioElement.preload = 'auto';
    
    // 🔧 VERSÃO ATUAL: Adicionar listener para atualizar estado global quando tocar
    globalAudioElement.addEventListener('play', () => {
      console.log('🎵 [GLOBAL] Audio play event - isPlaying = true');
      globalAudioState.isPlaying = true;
    });
    globalAudioElement.addEventListener('pause', () => {
      console.log('🎵 [GLOBAL] Audio pause event - isPlaying = false');
      globalAudioState.isPlaying = false;
    });
  }
  return globalAudioElement;
};

// Lista de domínios incorretos para auto-correção
const WRONG_DOMAINS = [
  'https://admin-panel-upgrade-27.preview.emergentagent.com',
  'http://secure-abri.preview.emergentagent.com'
];

// Função para obter URL correta do backend (VERSÃO ATUAL)
const getCorrectBackendUrl = () => {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Localhost = usar .env
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  // Preview/Produção = SEMPRE usar origin atual
  // NÃO usar .env pois pode estar desatualizado
  return origin;
};

// Função para corrigir URL de áudio
const fixAudioUrl = (url) => {
  if (!url) return url;
  const correctBase = getCorrectBackendUrl();
  for (const wrongDomain of WRONG_DOMAINS) {
    if (url.includes(wrongDomain.replace(/\/$/, ''))) {
      const fixed = url.replace(wrongDomain.replace(/\/$/, ''), correctBase);
      console.warn('🔧 [RadioPlayer] URL CORRIGIDA:', url, '→', fixed);
      return fixed;
    }
  }
  return url;
};

// 🔧 CONSTANTES DE RETRY LOGIC (VERSÃO ATUAL)
const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 2000; // 2 segundos base
const RETRY_DELAY_NETWORK = 3000; // 3 segundos para erro de rede
const SKIP_DELAY_FORMAT_ERROR = 800; // 🔧 8.1: Skip rápido para formato não suportado
const SKIP_DELAY_NETWORK_ERROR = 1500; // 🔧 8.1: Skip após esgotar retries

const RadioPlayerContext = createContext(null);

export const useRadioPlayer = () => {
  const context = useContext(RadioPlayerContext);
  if (!context) {
    throw new Error('useRadioPlayer must be used within a RadioPlayerProvider');
  }
  return context;
};

export const RadioPlayerProvider = ({ children }) => {
  // Estado do player
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [volume, setVolume] = useState(globalAudioState.volume);
  const [isMuted, setIsMuted] = useState(false); // 🔊 VERSÃO ATUAL: Estado de mudo
  const [volumeBeforeMute, setVolumeBeforeMute] = useState(0.8); // Volume antes de mutar
  
  // 🎧 VERSÃO ATUAL: Callback customizável para quando a música terminar
  const onTrackEndCallbackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0); // 🔧 Contador de retries
  const [failedTracks, setFailedTracks] = useState(new Set()); // 🔧 Músicas que falharam (evitar loop)
  
  // 🔧 VERSÃO ATUAL: Usar elemento de áudio GLOBAL
  const audioRef = useRef(getGlobalAudio());
  
  // 🔧 VERSÃO ATUAL: REFS PARA EVITAR STALE CLOSURES
  // Todos os estados usados em event listeners precisam ter refs
  const currentTrackRef = useRef(null);
  const isPlayingRef = useRef(false);
  const isVisibleRef = useRef(false);
  
  // 🔧 VERSÃO ATUAL: Restaurar estado do áudio global ao montar
  useEffect(() => {
    console.log('🎵 [9.0] RadioPlayerProvider montado, verificando estado global...');
    console.log('🎵 [9.0] globalAudioState:', globalAudioState);
    console.log('🎵 [9.0] audioRef.current.paused:', audioRef.current?.paused);
    console.log('🎵 [9.0] audioRef.current.src:', audioRef.current?.src);
    
    // Se o áudio global está tocando, restaurar estado
    if (audioRef.current && !audioRef.current.paused && audioRef.current.src) {
      console.log('🎵 [9.0] Áudio global está tocando! Restaurando estado...');
      if (globalAudioState.currentTrack) {
        setCurrentTrack(globalAudioState.currentTrack);
        currentTrackRef.current = globalAudioState.currentTrack;
      }
      setIsPlaying(true);
      isPlayingRef.current = true;
      setIsVisible(true);
      isVisibleRef.current = true;
      if (globalAudioState.playlist.length > 0) {
        setPlaylist(globalAudioState.playlist);
        setCurrentIndex(globalAudioState.currentIndex);
      }
    } else if (globalAudioState.currentTrack && !currentTrack) {
      console.log('🎵 [9.0] Restaurando estado do áudio global (pausado)...');
      setCurrentTrack(globalAudioState.currentTrack);
      currentTrackRef.current = globalAudioState.currentTrack;
      setIsPlaying(globalAudioState.isPlaying);
      isPlayingRef.current = globalAudioState.isPlaying;
      setPlaylist(globalAudioState.playlist);
      setCurrentIndex(globalAudioState.currentIndex);
      setIsVisible(true);
      isVisibleRef.current = true;
    }
  }, []);
  
  // 🔧 VERSÃO ATUAL: Sincronizar refs com state
  useEffect(() => {
    currentTrackRef.current = currentTrack;
    globalAudioState.currentTrack = currentTrack;
  }, [currentTrack]);
  
  useEffect(() => {
    isPlayingRef.current = isPlaying;
    globalAudioState.isPlaying = isPlaying;
  }, [isPlaying]);
  
  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);
  
  // Playlist para próxima/anterior - usando STATE para UI e REF para callbacks
  const [playlist, setPlaylist] = useState(globalAudioState.playlist);
  const [currentIndex, setCurrentIndex] = useState(globalAudioState.currentIndex);
  
  // 🔧 CORREÇÃO: Refs para evitar stale closures nos event listeners
  const playlistRef = useRef([]);
  const currentIndexRef = useRef(-1);
  const volumeRef = useRef(0.8);
  const retryCountRef = useRef(0); // 🔧 Ref para retry count
  const failedTracksRef = useRef(new Set()); // 🔧 Ref para músicas que falharam
  
  // Sincronizar refs com state e global
  useEffect(() => {
    playlistRef.current = playlist;
    globalAudioState.playlist = playlist;
  }, [playlist]);
  
  useEffect(() => {
    currentIndexRef.current = currentIndex;
    globalAudioState.currentIndex = currentIndex;
  }, [currentIndex]);
  
  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);
  
  // 🔧 Sincronizar retry count ref
  useEffect(() => {
    retryCountRef.current = retryCount;
  }, [retryCount]);
  
  // 🔧 RETRY LOGIC: Função para tentar recarregar a música atual
  const retryCurrentTrack = useCallback(() => {
    const currentRetries = retryCountRef.current;
    const tracks = playlistRef.current;
    const idx = currentIndexRef.current;
    
    if (currentRetries < MAX_RETRIES && tracks.length > 0 && idx >= 0 && idx < tracks.length) {
      const track = tracks[idx];
      const newRetryCount = currentRetries + 1;
      
      console.log(`🔄 [Retry ${newRetryCount}/${MAX_RETRIES}] Tentando recarregar: ${track.title}`);
      setRetryCount(newRetryCount);
      retryCountRef.current = newRetryCount;
      setError(`Reconectando... (tentativa ${newRetryCount}/${MAX_RETRIES})`);
      setIsLoading(true);
      
      // Recarregar o áudio
      if (audioRef.current) {
        audioRef.current.load();
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('✅ Retry bem-sucedido!');
              setIsPlaying(true);
              setIsLoading(false);
              setError(null);
              setRetryCount(0);
              retryCountRef.current = 0;
            })
            .catch((error) => {
              console.error('❌ Retry falhou:', error);
              // O evento de erro vai lidar com mais retries
            });
        }
      }
      return true;
    }
    return false;
  }, []);
  
  // 🔧 PROTEÇÃO ANTI-LOOP: Encontrar próxima música que não falhou
  const findNextValidTrack = useCallback((currentIdx) => {
    const tracks = playlistRef.current;
    const failedSet = failedTracksRef.current;
    
    // Percorrer todas as músicas começando da próxima
    for (let i = 1; i <= tracks.length; i++) {
      const nextIdx = (currentIdx + i) % tracks.length;
      const track = tracks[nextIdx];
      const trackId = track?.id || track?.audioUrl;
      
      if (trackId && !failedSet.has(trackId)) {
        return nextIdx;
      }
    }
    
    return -1; // Nenhuma música válida encontrada
  }, []);
  
  // Função para tocar uma track pelo índice - usando ref
  const playTrackByIndex = useCallback((index) => {
    const tracks = playlistRef.current;
    if (tracks.length === 0 || index < 0 || index >= tracks.length) {
      console.log('🎵 Playlist vazia ou índice inválido, parando...');
      return false;
    }
    
    const track = tracks[index];
    if (!track || !track.audioUrl) {
      console.error('Track inválida no índice:', index);
      return false;
    }
    
    // 🔧 PROTEÇÃO ANTI-LOOP: Verificar se essa música já falhou
    const trackId = track.id || track.audioUrl;
    if (failedTracksRef.current.has(trackId)) {
      console.log('⚠️ [AntiLoop] Música já falhou anteriormente, pulando:', track.title);
      // Tentar próxima música que não falhou
      const nextValidIndex = findNextValidTrack(index);
      if (nextValidIndex !== -1 && nextValidIndex !== index) {
        setTimeout(() => playTrackByIndex(nextValidIndex), 100);
      } else {
        console.log('❌ [AntiLoop] Nenhuma música válida na playlist');
        setError('Nenhuma música disponível no momento');
      }
      return false;
    }
    
    // 🔧 CORREÇÃO: Verificar e corrigir URL se necessário
    const correctedUrl = fixAudioUrl(track.audioUrl);
    if (correctedUrl !== track.audioUrl) {
      track.audioUrl = correctedUrl;
      console.log('🔧 [RadioPlayer] URL da track corrigida');
    }
    
    console.log('🎵 [RadioPlayer] Tocando:', track.title, 'URL:', track.audioUrl);
    
    // 🔧 RESET: Limpar contador de retries ao mudar de música
    setRetryCount(0);
    retryCountRef.current = 0;
    
    setError(null);
    setIsLoading(true);
    
    // Parar música atual se houver
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Definir nova música
    setCurrentTrack(track);
    setCurrentIndex(index);
    currentIndexRef.current = index;
    setIsVisible(true);
    
    // Carregar e tocar
    audioRef.current.src = track.audioUrl;
    audioRef.current.load();
    
    console.log('🎵 Carregando música:', track.title, 'índice:', index);
    
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          console.log('▶️ Tocando:', track.title);
        })
        .catch((error) => {
          console.error('Erro ao tocar:', error);
          setIsPlaying(false);
          setIsLoading(false);
          if (error.name === 'NotAllowedError') {
            setError('Toque no botão play para iniciar');
          }
        });
    }
    
    return true;
  }, []);
  
  // 🔧 CORREÇÃO: Handler de fim de música com acesso aos refs atuais
  // 🎧 VERSÃO ATUAL: Usa callback customizada se definida (para sistema de buffer)
  const handleEnded = useCallback(() => {
    console.log('🎵 Música terminou, verificando...');
    setIsPlaying(false);
    
    // 🎧 VERSÃO ATUAL: Se há callback customizada, usá-la (sistema de buffer)
    if (onTrackEndCallbackRef.current) {
      console.log('🎧 Usando callback customizada (buffer da rádio)');
      setTimeout(() => {
        onTrackEndCallbackRef.current();
      }, 300); // Pequeno delay para transição suave
      return;
    }
    
    // Fallback: sistema antigo de playlist linear
    const tracks = playlistRef.current;
    const idx = currentIndexRef.current;
    
    console.log('🎵 Playlist:', tracks.length, 'músicas, índice atual:', idx);
    
    // Auto-play próxima música se houver
    if (tracks.length > 0 && idx < tracks.length - 1) {
      const nextIdx = idx + 1;
      console.log('🎵 Tocando próxima música, índice:', nextIdx);
      setTimeout(() => {
        playTrackByIndex(nextIdx);
      }, 500); // Pequeno delay para transição suave
    } else if (tracks.length > 0) {
      // Volta ao início da playlist (loop)
      console.log('🎵 Fim da playlist, voltando ao início...');
      setTimeout(() => {
        playTrackByIndex(0);
      }, 500);
    } else {
      console.log('🎵 Playlist vazia, parando.');
    }
  }, [playTrackByIndex]);
  
  // Handlers de eventos do áudio
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  }, []);
  
  // Atualização mais frequente do progresso para sincronização de letras
  useEffect(() => {
    if (!isPlaying || !audioRef.current) return;
    
    const interval = setInterval(() => {
      if (audioRef.current) {
        setProgress(audioRef.current.currentTime);
      }
    }, 100); // Atualizar a cada 100ms para sincronização suave
    
    return () => clearInterval(interval);
  }, [isPlaying]);
  
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);
  
  const handleError = useCallback((e) => {
    console.error('Audio error:', e);
    setIsPlaying(false);
    setIsLoading(false);
    
    // Obter código de erro de mídia
    const mediaErrorCode = e?.target?.error?.code;
    const playbackTime = audioRef.current?.currentTime || 0;
    
    // 🔧 VERSÃO ATUAL: Mensagens de erro mais descritivas
    const errorMessages = {
      1: 'Reprodução cancelada',
      2: 'Erro de rede - reconectando...',
      3: 'Erro ao decodificar áudio - arquivo corrompido',
      4: 'Formato não suportado - pulando...'  // 🔧 8.1: Mensagem mais clara
    };
    
    const errorMessage = errorMessages[mediaErrorCode] || 'Erro desconhecido ao carregar áudio';
    
    // 🐛 REGISTRAR ERRO NO SISTEMA DE MONITORAMENTO
    const currentTrackData = playlistRef.current[currentIndexRef.current];
    logAudioError(e, {
      trackId: currentTrackData?.id,
      trackTitle: currentTrackData?.title,
      audioUrl: currentTrackData?.audioUrl,
      playbackTime: playbackTime,
      errorCode: mediaErrorCode,
      retryCount: retryCountRef.current
    });
    
    console.error(`🔊 Erro de áudio [${mediaErrorCode}]: ${errorMessage}`);
    console.error(`   Track: ${currentTrackData?.title}`);
    console.error(`   URL: ${currentTrackData?.audioUrl?.substring(0, 80)}...`);
    console.error(`   PlaybackTime: ${playbackTime}s`);
    console.error(`   Retry: ${retryCountRef.current}/${MAX_RETRIES}`);
    
    const currentRetries = retryCountRef.current;
    const tracks = playlistRef.current;
    const idx = currentIndexRef.current;
    
    // 🔧 VERSÃO ATUAL: TRATAMENTO ESPECÍFICO POR TIPO DE ERRO
    
    // ============================================================
    // ERRO 4: MEDIA_ERR_SRC_NOT_SUPPORTED - Formato não suportado
    // ============================================================
    // 🔧 VERSÃO ATUAL: Melhor tratamento - pode ser erro de rede também
    if (mediaErrorCode === 4) {
      // Se playbackTime > 0, é provável que seja erro de rede, não formato
      if (playbackTime > 0 && currentRetries < MAX_RETRIES) {
        console.log('🔄 [9.0] Erro 4 durante reprodução - tentando reconectar...');
        setError(`Reconectando... (${currentRetries + 1}/${MAX_RETRIES})`);
        
        const retryDelay = RETRY_DELAY_NETWORK * (currentRetries + 1);
        setTimeout(() => {
          retryCurrentTrack();
        }, retryDelay);
        return;
      }
      
      console.log('⏭️ [9.0] Formato não suportado ou falha de conexão - pulando');
      setError('Carregando próxima...');
      setRetryCount(0);
      retryCountRef.current = 0;
      
      if (tracks.length > 0) {
        const nextIdx = idx < tracks.length - 1 ? idx + 1 : 0;
        console.log(`🎵 Pulando para música ${nextIdx + 1} em ${SKIP_DELAY_FORMAT_ERROR}ms...`);
        setTimeout(() => {
          playTrackByIndex(nextIdx);
        }, SKIP_DELAY_FORMAT_ERROR);
      }
      return;
    }
    
    // ============================================================
    // ERRO 3: MEDIA_ERR_DECODE - Arquivo corrompido
    // ============================================================
    // PULAR IMEDIATAMENTE - arquivo não vai funcionar
    if (mediaErrorCode === 3) {
      console.log('⏭️ [8.1] Erro de decodificação - pulando imediatamente');
      setError('Carregando próxima...');
      setRetryCount(0);
      retryCountRef.current = 0;
      
      if (tracks.length > 0) {
        const nextIdx = idx < tracks.length - 1 ? idx + 1 : 0;
        setTimeout(() => {
          playTrackByIndex(nextIdx);
        }, SKIP_DELAY_FORMAT_ERROR);
      }
      return;
    }
    
    // ============================================================
    // ERRO 2: MEDIA_ERR_NETWORK - Erro de rede
    // ============================================================
    // 🔧 8.1: RETRY INTELIGENTE - só se música estava tocando
    if (mediaErrorCode === 2) {
      // Se playbackTime > 0, a música estava tocando = problema de rede
      // Se playbackTime = 0, erro na inicialização = provavelmente arquivo não existe
      if (playbackTime > 0 && currentRetries < MAX_RETRIES) {
        // Erro durante reprodução - vale a pena tentar reconectar
        const retryDelay = RETRY_DELAY_NETWORK * (currentRetries + 1);
        console.log(`🔄 [8.1] Erro de rede durante reprodução. Reconectando em ${retryDelay/1000}s...`);
        setError(`Reconectando... (${currentRetries + 1}/${MAX_RETRIES})`);
        
        setTimeout(() => {
          retryCurrentTrack();
        }, retryDelay);
        return;
      } else if (playbackTime === 0) {
        // Erro na inicialização - arquivo pode não existir, pular
        console.log('⏭️ [8.1] Erro de rede na inicialização - pulando (arquivo pode não existir)');
        setError('Carregando próxima...');
        
        // 🔧 ANTI-LOOP: Marcar música como falha
        const currentTrackData = currentTrackRef.current;
        const trackId = currentTrackData?.id || currentTrackData?.audioUrl;
        if (trackId) {
          failedTracksRef.current.add(trackId);
          console.log(`⛔ [AntiLoop] Música marcada como falha: ${trackId}`);
        }
      } else {
        // Esgotou retries
        console.error(`❌ [8.1] Máximo de tentativas (${MAX_RETRIES}). Pulando...`);
        setError('Carregando próxima...');
      }
    }
    
    // ============================================================
    // FALLBACK: Outros erros ou após esgotar retries
    // ============================================================
    
    // 🔧 ANTI-LOOP: Marcar música como falha (para qualquer erro não recuperável)
    const failedTrackData = currentTrackRef.current;
    const failedTrackId = failedTrackData?.id || failedTrackData?.audioUrl;
    if (failedTrackId && (mediaErrorCode === 3 || mediaErrorCode === 4 || (mediaErrorCode === 2 && playbackTime === 0))) {
      failedTracksRef.current.add(failedTrackId);
      console.log(`⛔ [AntiLoop] Música marcada como falha permanente: ${failedTrackId}`);
    }
    
    setRetryCount(0);
    retryCountRef.current = 0;
    
    if (!errorMessages[mediaErrorCode]) {
      setError(errorMessage);
    }
    
    // 🔧 ANTI-LOOP: Usar findNextValidTrack em vez de simples incremento
    const skipDelay = mediaErrorCode === 2 ? SKIP_DELAY_NETWORK_ERROR : SKIP_DELAY_FORMAT_ERROR;
    
    if (tracks.length > 0) {
      const nextIdx = findNextValidTrack(idx);
      if (nextIdx !== -1) {
        console.log(`🎵 [AntiLoop] Pulando para música válida ${nextIdx + 1} em ${skipDelay}ms...`);
        setTimeout(() => {
          playTrackByIndex(nextIdx);
        }, skipDelay);
      } else {
        console.log('❌ [AntiLoop] Todas as músicas falharam');
        setError('Nenhuma música disponível');
      }
    }
  }, [playTrackByIndex, retryCurrentTrack, findNextValidTrack]);
  
  const handlePlaying = useCallback(() => {
    setIsLoading(false);
  }, []);
  
  const handleWaiting = useCallback(() => {
    setIsLoading(true);
  }, []);
  
  // 🔧 VERSÃO ATUAL: Inicializar event listeners do áudio GLOBAL
  // O elemento de áudio já foi criado fora do React (getGlobalAudio)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      console.error('❌ Audio element não disponível!');
      return;
    }
    
    console.log('🎵 [9.0] Configurando event listeners do áudio global...');
    
    // Adicionar event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('waiting', handleWaiting);
    
    // 🔧 VERSÃO ATUAL: Se o áudio já está tocando, sincronizar o estado
    if (!audio.paused && audio.src) {
      console.log('🎵 [9.0] Áudio já está tocando, sincronizando estado...');
      setIsPlaying(true);
      setIsVisible(true);
    }
    
    // Cleanup - NÃO pausar o áudio no cleanup para manter tocando
    return () => {
      console.log('🎵 [9.0] Removendo event listeners (áudio continua tocando)...');
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('waiting', handleWaiting);
      // IMPORTANTE: NÃO pausar aqui para manter a música tocando durante navegação
    };
  }, []); // 🔧 Dependências vazias para executar apenas uma vez
  
  // Atualizar volume quando mudar
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      globalAudioState.volume = volume;
    }
  }, [volume]);
  
  // 🔧 VERSÃO ATUAL: Garantir que isVisible seja true sempre que houver currentTrack
  // Isso evita que a barra da rádio suma ao navegar entre páginas
  useEffect(() => {
    if (currentTrack && !isVisible) {
      console.log('🔧 [9.0] Restaurando visibilidade do player - track existe');
      setIsVisible(true);
    }
  }, [currentTrack, isVisible]);

  /**
   * Tocar uma música específica
   * @param {Object} track - { id, title, author, audioUrl, coverUrl }
   */
  const playTrack = useCallback((track) => {
    // Validação robusta do track
    if (!track) {
      console.error('❌ Track inválida: track é null/undefined');
      logAudioError(new Error('Track é null/undefined'), {
        trackId: null,
        trackTitle: null,
        audioUrl: null,
        errorCode: 'NO_TRACK'
      });
      return;
    }
    
    if (!track.audioUrl) {
      console.error('❌ Track sem audioUrl:', track.title || track.id);
      logAudioError(new Error('Track sem audioUrl'), {
        trackId: track.id,
        trackTitle: track.title,
        audioUrl: null,
        errorCode: 'NO_AUDIO_URL'
      });
      // Tentar próxima música se houver playlist
      const tracks = playlistRef.current;
      const idx = currentIndexRef.current;
      if (tracks.length > 0 && idx < tracks.length - 1) {
        console.log('🎵 Pulando para próxima música (track atual sem áudio)...');
        setTimeout(() => {
          playTrackByIndex(idx + 1);
        }, 500);
      }
      return;
    }
    
    setError(null);
    setIsLoading(true);
    
    // Parar música atual se houver
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Definir nova música
    setCurrentTrack(track);
    setIsVisible(true);
    
    // Carregar e tocar
    audioRef.current.src = track.audioUrl;
    audioRef.current.load();
    
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error('Erro ao tocar:', error);
          setIsPlaying(false);
          setIsLoading(false);
          if (error.name === 'NotAllowedError') {
            setError('Toque no botão play para iniciar');
          }
        });
    }
  }, []);
  
  /**
   * Definir playlist e tocar
   */
  const setPlaylistAndPlay = useCallback((tracks, startIndex = 0) => {
    if (!tracks || tracks.length === 0) return;
    
    // 🔊 FILTRAR APENAS TRACKS COM AUDIOURL VÁLIDA E CORRIGIR URLs
    const validTracks = tracks
      .filter(t => t && t.audioUrl)
      .map(t => ({
        ...t,
        audioUrl: fixAudioUrl(t.audioUrl) // Corrigir URL automaticamente
      }));
    
    if (validTracks.length === 0) {
      console.error('❌ Nenhuma track válida com audioUrl na playlist');
      return;
    }
    
    console.log('🎵 Definindo playlist com', validTracks.length, 'músicas válidas de', tracks.length, 'total, iniciando em', startIndex);
    console.log('🎵 Primeira URL:', validTracks[0]?.audioUrl);
    
    // Ajustar startIndex se tracks foram filtradas
    let adjustedIndex = startIndex;
    if (adjustedIndex >= validTracks.length) {
      adjustedIndex = 0;
    }
    
    // Atualizar state E refs
    setPlaylist(validTracks);
    playlistRef.current = validTracks;
    setCurrentIndex(adjustedIndex);
    currentIndexRef.current = adjustedIndex;
    
    // Tocar a música
    playTrackByIndex(adjustedIndex);
  }, [playTrackByIndex]);
  
  /**
   * Toggle Play/Pause
   */
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !currentTrack) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.error('Erro ao retomar:', error);
          });
      }
    }
  }, [isPlaying, currentTrack]);
  
  /**
   * 🔧 VERSÃO ATUAL: Pausar sem parar completamente
   * Usado quando outra fonte de áudio precisa tocar (ex: página de perfil da música)
   */
  const pause = useCallback(() => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      console.log('⏸️ [9.0] Rádio pausada por solicitação externa');
    }
  }, [isPlaying]);
  
  /**
   * Parar completamente e esconder player
   */
  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
    }
    setIsPlaying(false);
    setCurrentTrack(null);
    setIsVisible(false);
    setProgress(0);
    setDuration(0);
    setError(null);
    // Limpar playlist
    setPlaylist([]);
    playlistRef.current = [];
    setCurrentIndex(-1);
    currentIndexRef.current = -1;
  }, []);
  
  /**
   * Próxima música
   */
  const playNext = useCallback(() => {
    const tracks = playlistRef.current;
    const idx = currentIndexRef.current;
    
    if (tracks.length === 0) return;
    
    const nextIndex = (idx + 1) % tracks.length;
    console.log('⏭️ Próxima música, índice:', nextIndex);
    playTrackByIndex(nextIndex);
  }, [playTrackByIndex]);
  
  /**
   * Música anterior
   */
  const playPrevious = useCallback(() => {
    const tracks = playlistRef.current;
    const idx = currentIndexRef.current;
    
    if (tracks.length === 0) return;
    
    const prevIndex = idx > 0 ? idx - 1 : tracks.length - 1;
    console.log('⏮️ Música anterior, índice:', prevIndex);
    playTrackByIndex(prevIndex);
  }, [playTrackByIndex]);
  
  /**
   * Seek para posição específica
   */
  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setProgress(time);
    }
  }, []);
  
  /**
   * Ajustar volume (0 a 1)
   */
  const setPlayerVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolume(clampedVolume);
    volumeRef.current = clampedVolume;
    if (audioRef.current) {
      audioRef.current.volume = clampedVolume;
    }
  }, []);
  
  /**
   * Minimizar player (manter tocando mas esconder)
   */
  const minimize = useCallback(() => {
    setIsVisible(false);
  }, []);
  
  /**
   * Maximizar player (mostrar novamente)
   */
  const maximize = useCallback(() => {
    if (currentTrack) {
      setIsVisible(true);
    }
  }, [currentTrack]);
  
  /**
   * 🔊 VERSÃO ATUAL: Alternar MUDO (mute/unmute)
   * Silencia o áudio sem pausar a música
   */
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      if (isMuted) {
        // Desmutar - restaurar volume anterior
        audioRef.current.volume = volumeBeforeMute;
        setVolume(volumeBeforeMute);
        setIsMuted(false);
        console.log('🔊 [8.1] Áudio desmutado - Volume:', volumeBeforeMute);
      } else {
        // Mutar - salvar volume atual e zerar
        setVolumeBeforeMute(volume);
        audioRef.current.volume = 0;
        setIsMuted(true);
        console.log('🔇 [8.1] Áudio mutado');
      }
    }
  }, [isMuted, volume, volumeBeforeMute]);
  
  /**
   * 🎧 VERSÃO ATUAL: Definir callback para quando a música terminar
   * Usado pelo sistema de buffer da rádio para controlar a próxima música
   */
  const setOnTrackEndCallback = useCallback((callback) => {
    onTrackEndCallbackRef.current = callback;
    console.log('🎧 Callback de fim de música definida:', callback ? 'ativa' : 'desativada');
  }, []);
  
  const value = {
    // Estado
    currentTrack,
    isPlaying,
    isVisible,
    volume,
    isMuted, // 🔊 VERSÃO ATUAL: Estado de mudo
    progress,
    duration,
    isLoading,
    error,
    playlist,
    currentIndex,
    
    // Ações
    playTrack,
    setPlaylistAndPlay,
    togglePlayPause,
    pause, // 🔧 VERSÃO ATUAL: Pausar rádio sem parar
    toggleMute, // 🔊 VERSÃO ATUAL: Função de mudo
    stop,
    playNext,
    playPrevious,
    seekTo,
    setVolume: setPlayerVolume,
    minimize,
    maximize,
    
    // 🎧 VERSÃO ATUAL: Callback de fim de música
    setOnTrackEndCallback,
    
    // Ref do áudio (para casos especiais)
    audioRef
  };
  
  return (
    <RadioPlayerContext.Provider value={value}>
      {children}
    </RadioPlayerContext.Provider>
  );
};

export default RadioPlayerContext;
